import React from 'react';
import DodTermText from '../DodTermText';
import { recommendedWorkRank } from '../constants/complianceChecklists';
import type { DodGlossaryTermId } from '../dodGlossary';
import HelpBadge from './HelpBadge';

export type KpiLane = 'done' | 'blocked' | 'ready';

export type KpiTaskRow = {
  id: string;
  title: string;
  action: string;
  section: string;
  priority?: 'blocking' | 'required' | 'recommended';
  lane: KpiLane;
  status: 'not_started' | 'in_progress' | 'reviewed' | 'gap';
  reviewedAt?: string;
  dueDate?: string;
  openDeps?: number;
  unlocks?: number;
  waitsOutside?: boolean;
};

export type KpiCalendarRow = {
  id: string;
  title: string;
  date: string;
};

export type KpiSectionRow = {
  id: string;
  label: string;
  total: number;
};

type ComplianceKpiDashboardProps = {
  done: number;
  blocked: number;
  open: number;
  gaps: number;
  levelLabel?: string;
  language: 'en' | 'es';
  hiddenWidgets?: ReadonlySet<string>;
  selectedLane?: KpiLane;
  onSelectLane?: (lane: KpiLane) => void;
  onOpenTask?: (id: string) => void;
  /** Run Do-it-for-me for the selected ready-lane task ids. */
  onBulkDoTasks?: (ids: string[]) => void | Promise<void>;
  bulkDoBusy?: boolean;
  tasks: KpiTaskRow[];
  events: KpiCalendarRow[];
  sections: KpiSectionRow[];
};

const SECTION_COLORS = ['#3d7ec9', '#2f9e6b', '#d9892b', '#c4524a', '#7a6bb5', '#4aa3a3'];
const MONTHS = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  es: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
};

const todayKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const dateKey = (value: string): string => (value || '').slice(0, 10);

const ComplianceKpiDashboard: React.FC<ComplianceKpiDashboardProps> = ({
  done,
  blocked,
  open,
  gaps,
  levelLabel,
  language,
  hiddenWidgets,
  selectedLane,
  onSelectLane,
  onOpenTask,
  onBulkDoTasks,
  bulkDoBusy = false,
  tasks,
  events,
  sections,
}) => {
  const copy = language === 'es';
  const showWidget = (id: string) => !hiddenWidgets?.has(id);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const seen = new Set<DodGlossaryTermId>();
  const total = done + blocked + open;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const today = todayKey();
  const dated = events
    .filter((event) => /^\d{4}-\d{2}-\d{2}$/.test(dateKey(event.date)))
    .slice()
    .sort((left, right) => dateKey(left.date).localeCompare(dateKey(right.date)));
  const year = dated.find((event) => dateKey(event.date) >= today)?.date.slice(0, 4)
    || dated[0]?.date.slice(0, 4)
    || today.slice(0, 4);
  const blockingLeft = tasks.filter((task) => task.priority === 'blocking' && task.lane !== 'done').length;
  const activity = tasks
    .filter((task) => task.status === 'reviewed' && dateKey(task.reviewedAt || ''))
    .slice()
    .sort((left, right) => dateKey(right.reviewedAt || '').localeCompare(dateKey(left.reviewedAt || '')))
    .slice(0, 6);
  const readyPool = tasks
    .filter((task) => task.lane === 'ready' && task.status !== 'reviewed')
    .slice()
    .sort((left, right) => recommendedWorkRank(left).localeCompare(recommendedWorkRank(right)) || left.title.localeCompare(right.title));
  const myTasks = (readyPool.length
    ? readyPool
    : tasks
      .filter((task) => task.lane !== 'done' && task.status !== 'reviewed')
      .slice()
      .sort((left, right) => recommendedWorkRank(left).localeCompare(recommendedWorkRank(right)) || left.title.localeCompare(right.title))
  ).slice(0, onBulkDoTasks ? 12 : 6);
  const selectableIds = myTasks.filter((task) => task.lane === 'ready').map((task) => task.id);
  const selectedReady = selectedIds.filter((id) => selectableIds.includes(id));
  const allReadySelected = selectableIds.length > 0 && selectableIds.every((id) => selectedReady.includes(id));

  const selectableKey = selectableIds.join('|');
  React.useEffect(() => {
    const allowed = new Set(selectableKey ? selectableKey.split('|') : []);
    setSelectedIds((current) => current.filter((id) => allowed.has(id)));
  }, [selectableKey]);
  const slices = sections.filter((section) => section.total > 0);
  const sliceTotal = slices.reduce((sum, section) => sum + section.total, 0);
  let cursor = 0;
  const gradient = slices.map((section, index) => {
    const start = cursor;
    const share = sliceTotal ? (section.total / sliceTotal) * 100 : 0;
    cursor += share;
    return `${SECTION_COLORS[index % SECTION_COLORS.length]} ${start}% ${cursor}%`;
  }).join(', ');
  const monthCounts = MONTHS.en.map((_, index) => {
    const key = `${year}-${String(index + 1).padStart(2, '0')}`;
    return dated.filter((event) => dateKey(event.date).startsWith(key)).length;
  });
  const monthMax = Math.max(1, ...monthCounts);
  const filters: Array<{ key: string; value: number; label: string; tone: 'done' | 'blocked' | 'ready'; lane: KpiLane }> = [
    { key: 'open', value: open, label: copy ? 'Abiertas' : 'Open', tone: 'ready', lane: 'ready' },
    { key: 'blocked', value: blocked, label: copy ? 'Bloqueadas' : 'Blocked', tone: 'blocked', lane: 'blocked' },
    { key: 'done', value: done, label: copy ? 'Hechas' : 'Done', tone: 'done', lane: 'done' },
  ];
  const facts: Array<{ key: string; value: number; label: string }> = [
    { key: 'gaps', value: gaps, label: copy ? 'Brechas' : 'Gaps' },
    { key: 'dated', value: dated.length, label: copy ? 'Con fecha' : 'With a date' },
    { key: 'blocking', value: blockingLeft, label: copy ? 'Bloquean el trabajo' : 'Blocking' },
  ];
  const priorityLabel = (priority?: KpiTaskRow['priority']) => {
    if (priority === 'blocking') return copy ? 'Bloquea' : 'Blocking';
    if (priority === 'required') return copy ? 'Requerida' : 'Required';
    if (priority === 'recommended') return copy ? 'Sugerida' : 'Suggested';
    return '';
  };
  const statusLabel = (task: KpiTaskRow) => {
    if (task.status === 'reviewed') return copy ? 'Hecho' : 'Done';
    if (task.status === 'in_progress') return copy ? 'En curso' : 'In progress';
    if (task.status === 'gap') return copy ? 'Brecha' : 'Gap';
    if (task.lane === 'blocked') return copy ? 'Bloqueado' : 'Blocked';
    return copy ? 'Pendiente' : 'Not started';
  };
  const statusTone = (task: KpiTaskRow) => {
    if (task.status === 'reviewed') return 'reviewed';
    if (task.status === 'in_progress') return 'in_progress';
    if (task.status === 'gap') return 'gap';
    if (task.lane === 'blocked') return 'blocked';
    return 'not_started';
  };
  return (
    <section className="dod-company-progress compliance-kpi-dashboard" aria-label={copy ? 'Progreso de cumplimiento' : 'Compliance progress'}>
      <div className="dod-progress-head">
        <h3>
          <DodTermText text={copy ? 'Progreso de cumplimiento — KPIs' : 'Compliance progress KPIs'} language={language} seen={seen} />
          <HelpBadge label={copy ? 'Qué miden estos números' : 'What these numbers mean'}>
            <p>
              {copy
                ? 'Abiertas, bloqueadas y hechas son tres carriles. Juntas son todos los pasos. Una brecha es un paso marcado como incompleto, así que ya está en uno de esos carriles. Con fecha cuenta fechas de presentación, no pasos extra. Bloquean cuenta pasos de los que depende otro trabajo, y esos pasos ya están en los carriles. Los gráficos son los mismos pasos, por grupo o por fecha. Esto no es un puntaje de auditoría.'
                : 'Open, blocked, and done are three lanes. Together they are every step. A gap is a step marked incomplete, so it is already in one of those lanes. With a date counts filing dates, not extra steps. Blocking counts steps that later work waits on, and those steps are already in the lanes. The charts are the same steps, split by group or by date. This is not an audit score.'}
            </p>
          </HelpBadge>
        </h3>
        {levelLabel ? <span className="dod-progress-level">{levelLabel}</span> : null}
      </div>
      <p className="dod-progress-note">
        {copy
          ? `${percent}% de ${total} tareas están terminadas. ${gaps} ${gaps === 1 ? 'es una brecha' : 'son brechas'}. Esto no es un puntaje de auditoría.`
          : `${percent}% of ${total} tasks are finished. ${gaps} ${gaps === 1 ? 'is a gap' : 'are gaps'}. This is not an audit score.`}
      </p>
      <ul className="compliance-kpi-tiles" aria-label={copy ? 'Indicadores' : 'Indicators'}>
        {filters.map((metric) => {
          const selected = selectedLane === metric.lane;
          const name = copy
            ? `${metric.value} ${metric.label}. ${selected ? 'Mostrando estos pasos.' : 'Ver estos pasos.'}`
            : `${metric.value} ${metric.label}. ${selected ? 'Showing these steps.' : 'Show these steps.'}`;
          return (
            <li key={metric.key}>
              <button
                type="button"
                className={`dod-kpi compliance-kpi-tile dod-kpi--${metric.tone}${selected ? ' is-selected' : ''}`}
                aria-pressed={selected}
                aria-label={name}
                onClick={() => onSelectLane?.(metric.lane)}
              >
                <strong>{String(metric.value).padStart(2, '0')}</strong>
                <span>{metric.label}</span>
              </button>
            </li>
          );
        })}
        {facts.map((fact) => (
          <li key={fact.key}>
            <div className="compliance-kpi-tile">
              <strong>{String(fact.value).padStart(2, '0')}</strong>
              <span>{fact.label}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="compliance-kpi-board">
        {showWidget('due') ? (
        <section className="compliance-kpi-panel" aria-label={copy ? 'Tareas por fecha' : 'Tasks by due date'}>
          <h4>{copy ? 'Tareas por fecha' : 'Tasks by due date'}</h4>
          {dated.length ? (
            <table className="compliance-kpi-table">
              <thead>
                <tr>
                  <th>{copy ? 'Fecha' : 'Date'}</th>
                  <th>{copy ? 'Tarea' : 'Task'}</th>
                  <th>{copy ? 'Cuándo' : 'When'}</th>
                </tr>
              </thead>
              <tbody>
                {dated.map((event) => {
                  const days = Math.round((new Date(`${dateKey(event.date)}T12:00:00`).getTime() - new Date(`${today}T12:00:00`).getTime()) / 86400000);
                  const when = days < 0
                    ? (copy ? `Hace ${-days} días` : `${-days} days past`)
                    : days === 0
                      ? (copy ? 'Hoy' : 'Due today')
                      : (copy ? `En ${days} días` : `Due in ${days} days`);
                  return (
                    <tr key={event.id}>
                      <td>{dateKey(event.date)}</td>
                      <td><DodTermText text={event.title} language={language} seen={seen} /></td>
                      <td>
                        <span className={`compliance-kpi-status ${days < 0 ? 'is-late' : 'is-progress'}`}>
                          {when}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="compliance-kpi-empty">
              {copy
                ? 'Todavía no hay fechas. Guárdelas en el calendario de esta página.'
                : 'No dates yet. Save them on the calendar on this page.'}
            </p>
          )}
        </section>
        ) : null}
        {showWidget('ready') ? (
        <section className="compliance-kpi-panel" aria-label={copy ? 'Listo para hacer' : 'Ready to do'}>
          <div className="compliance-ready-head">
            <h4>{copy ? 'Listo para hacer' : 'Ready to do'}</h4>
            {onBulkDoTasks && selectableIds.length ? (
              <div className="compliance-ready-bulk" role="group" aria-label={copy ? 'Selección masiva' : 'Bulk selection'}>
                <label className="compliance-ready-select-all">
                  <input
                    type="checkbox"
                    checked={allReadySelected}
                    disabled={bulkDoBusy}
                    onChange={(event) => {
                      setSelectedIds(event.target.checked ? selectableIds : []);
                    }}
                  />
                  <span>
                    {copy
                      ? (allReadySelected ? 'Quitar todas' : 'Elegir todas')
                      : (allReadySelected ? 'Clear all' : 'Select all')}
                  </span>
                </label>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-primary compliance-ready-bulk-go"
                  disabled={bulkDoBusy || selectedReady.length === 0}
                  onClick={() => {
                    void onBulkDoTasks(selectedReady);
                  }}
                >
                  {bulkDoBusy
                    ? (copy ? 'Ejecutando…' : 'Working…')
                    : (copy
                      ? `Que NextCompli las haga (${selectedReady.length})`
                      : `Have NextCompli do these (${selectedReady.length})`)}
                </button>
              </div>
            ) : null}
          </div>
          {myTasks.length ? (
            <ul className="compliance-ready-list">
              {myTasks.map((task) => {
                const priority = priorityLabel(task.priority);
                const canSelect = Boolean(onBulkDoTasks) && task.lane === 'ready';
                const checked = selectedReady.includes(task.id);
                return (
                  <li key={task.id} className={`compliance-ready-row${checked ? ' is-selected' : ''}`}>
                    {canSelect ? (
                      <label className="compliance-ready-check">
                        <span className="visually-hidden">
                          {copy ? `Seleccionar ${task.title}` : `Select ${task.title}`}
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={bulkDoBusy}
                          onChange={(event) => {
                            const on = event.target.checked;
                            setSelectedIds((current) => (
                              on
                                ? Array.from(new Set([...current, task.id]))
                                : current.filter((id) => id !== task.id)
                            ));
                          }}
                        />
                      </label>
                    ) : null}
                    <button type="button" className="compliance-ready-card" onClick={() => onOpenTask?.(task.id)}>
                      <strong>{task.title}</strong>
                      <span className="compliance-ready-meta">
                        <DodTermText text={task.section} language={language} seen={seen} />
                        {priority ? (
                          <span className={`compliance-ready-chip priority-${task.priority || 'required'}`}>
                            {priority}
                          </span>
                        ) : null}
                        <span className={`compliance-ready-chip status-${statusTone(task)}`}>
                          {statusLabel(task)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="compliance-kpi-empty">{copy ? 'No quedan tareas abiertas.' : 'No open tasks left.'}</p>
          )}
        </section>
        ) : null}
      </div>
      {(showWidget('group-chart') || showWidget('activity') || (showWidget('months') && monthCounts.some((count) => count > 0))) ? (
        <details
          className="compliance-kpi-more"
          open={moreOpen}
          onToggle={(event) => setMoreOpen(event.currentTarget.open)}
        >
          <summary>
            <span>{copy ? 'Más detalle del progreso' : 'More progress detail'}</span>
            <span className="compliance-fold-hint">
              {copy
                ? 'Grupos, actividad reciente y fechas por mes. Cerrado al inicio.'
                : 'Groups, recent activity, and dates by month. Collapsed by default.'}
            </span>
          </summary>
          {showWidget('group-chart') || showWidget('activity') ? (
          <div className="compliance-kpi-board compliance-kpi-board--lower">
            {showWidget('group-chart') ? (
            <section className="compliance-kpi-panel" aria-label={copy ? 'Tareas por grupo' : 'Tasks by group'}>
              <h4>{copy ? 'Tareas por grupo' : 'Tasks by group'}</h4>
              {sliceTotal ? (
                <div className="compliance-kpi-pie-wrap">
                  <div
                    className="compliance-kpi-pie"
                    style={{ background: `conic-gradient(${gradient})` }}
                    role="img"
                    aria-label={slices.map((section) => {
                      const share = Math.round((section.total / sliceTotal) * 100);
                      return `${section.label} ${share}%`;
                    }).join(', ')}
                  />
                  <ul className="compliance-kpi-legend">
                    {slices.map((section, index) => (
                      <li key={section.id}>
                        <i style={{ background: SECTION_COLORS[index % SECTION_COLORS.length] }} />
                        <DodTermText text={section.label} language={language} seen={seen} />
                        <b>{Math.round((section.total / sliceTotal) * 100)}%</b>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="compliance-kpi-empty">{copy ? 'Todavía no hay grupos.' : 'No groups yet.'}</p>
              )}
            </section>
            ) : null}
            {showWidget('activity') ? (
            <section className="compliance-kpi-panel" aria-label={copy ? 'Actividad reciente' : 'Recent activity'}>
              <h4>{copy ? 'Actividad reciente' : 'Recent activity'}</h4>
              {activity.length ? (
                <ul className="compliance-kpi-activity">
                  {activity.map((task) => (
                    <li key={task.id}>
                      <DodTermText text={task.title} language={language} seen={seen} />
                      <time dateTime={dateKey(task.reviewedAt || '')}>{dateKey(task.reviewedAt || '')}</time>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="compliance-kpi-empty">
                  {copy
                    ? 'Ningún paso terminado tiene fecha todavía.'
                    : 'No finished step has a date yet.'}
                </p>
              )}
            </section>
            ) : null}
          </div>
          ) : null}
          {showWidget('months') && monthCounts.some((count) => count > 0) ? (
            <section className="compliance-kpi-panel" aria-label={copy ? `Fechas guardadas en ${year}` : `Dates saved in ${year}`}>
              <h4>{copy ? `Fechas guardadas en ${year}` : `Dates saved in ${year}`}</h4>
              <div className="compliance-kpi-months" role="img" aria-label={monthCounts.map((count, index) => `${MONTHS[language][index]} ${count}`).join(', ')}>
                {monthCounts.map((count, index) => (
                  <div key={MONTHS.en[index]}>
                    <span style={{ height: count ? `${Math.round((count / monthMax) * 100)}%` : '0%' }} />
                    <small>{MONTHS[language][index]}</small>
                  </div>
                ))}
              </div>
            </section>
          ) : (
            <section className="compliance-kpi-panel" aria-label={copy ? `Fechas guardadas en ${year}` : `Dates saved in ${year}`} hidden>
              <h4>{copy ? `Fechas guardadas en ${year}` : `Dates saved in ${year}`}</h4>
            </section>
          )}
        </details>
      ) : null}
    </section>
  );
};

export default ComplianceKpiDashboard;
